import React, { useEffect, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import BaseTemplate from "./BaseTemplate"; // Import your BaseTemplate
import Icon from "new_components/atoms/Icon";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import colors from "styles/colors";
// Using semantic tooltip for these buttons due to complexity.
import { Popup } from "semantic-ui-react";

const LayoutWrapper = styled.div`
  display: flex;
  flex: ${({ chatStarted }) => (chatStarted ? "none" : "1")};
  height: ${({ chatStarted }) => (chatStarted ? "100%" : "calc(100vh - 40px)")};
  position: relative;
  margin-top: ${({ chatStarted }) => (chatStarted ? "0px !important" : "0px")};
  padding-top: ${({ chatStarted }) => (chatStarted ? "0px !important" : "0px")};
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  padding-top: ${({ chatStarted }) => (chatStarted ? "0px !important" : "0px")};
  margin-top: ${({ chatStarted }) => (chatStarted ? "0px !important" : "0px")};
`;

const ChatContent = styled.div`
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
  padding-bottom: 100px;
`;

const ChatHeading = styled.div`
  display: ${({ chatStarted }) => (chatStarted ? "none" : "flex")};
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const InputBox = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  box-sizing: border-box;
`;

const OverlayHide = styled.div`
  height: 130px;
  width: 100%;
  position: fixed;
  background-color: #fff;
  bottom: 0;
  z-index: -1;
  pointer-events: none;
`;

const ChatScrollArea = styled.div`
  flex: 1;
  padding: ${({ chatStarted }) => (chatStarted ? "0px 16px 120px" : "16px")};
  scroll-behavior: smooth;
  overflow-y: auto;
  height: 100%;
  margin-top: ${({ chatStarted }) => (chatStarted ? "0px !important" : "0")};
  padding-top: ${({ chatStarted }) => (chatStarted ? "0px !important" : "16px")};
`;

const FixedInputBox = styled.div`
  position: fixed;
  bottom: 0px;
  left: calc(50% - 290px);
  width: 800px;
  z-index: ${props => props.modalOpen ? 999 : 1100};
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  background: ${props => props.modalOpen ? 'transparent' : 'linear-gradient(transparent, rgba(255, 255, 255, 0.9) 10%, white 40%)'};
  padding-top: 30px;
  height: 120px;
  padding-left: 16px;
  padding-right: 16px;
  
  > div {
    pointer-events: all;
    background: white;
    border-radius: 12px;
    width: 100%;
    margin: 0 auto;
    position: relative;
    z-index: ${props => props.modalOpen ? 999 : 1101};
    margin-bottom:60px;

  }
`;

const ChatBox = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${({ chatStarted }) => (chatStarted ? "flex-start" : "center")};
  padding: ${({ chatStarted }) => (chatStarted ? "0px 0px 16px" : "16px")};
  box-sizing: border-box;
  margin-top: ${({ chatStarted }) => (chatStarted ? "0px" : "0px")};
`;

const ScrollToBottomButton = styled.div`
  position: fixed;
  bottom: ${({ chatStarted }) => (chatStarted ? "140px" : "150px")};
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
  color: ${colors.primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: ${props => props.modalOpen ? 999 : 1102};
  display: ${({ show }) => (show ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  opacity: 1;
  &:hover {
    transform: translateX(-50%) scale(1.05);
  }
`;

const ChatTemplate = ({
  heading,
  pageTitle,
  subtitle,
  scrollable,
  chatHeading,
  chatContent,
  chatStarted,
  chatComponent,
  activeRoom,
  startNewChat,
  children,
  inputBox,
  modals,
  fromPulseSurvey,
  modalOpen,
}) => {
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  useEffect(() => {
    if (chatStarted) {
      setShowScrollToBottom(false);
    }
  }, [activeRoom, chatStarted]);

  useEffect(() => {
    // if new chat is clicked, reset the scroll to bottom button
    setShowScrollToBottom(false);
  }, [chatStarted]);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      const atBottom = scrollTop + clientHeight >= scrollHeight - 5;

      // Show scroll-to-bottom button when NOT at bottom and chat has started
      setShowScrollToBottom(!atBottom && chatStarted);
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [chatStarted]);

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth"
    });
  };


  return (
    <BaseTemplate
      heading={!chatStarted && heading}
      pageTitle={!chatStarted && pageTitle}
      subtitle={!chatStarted && subtitle}
      scrollable={chatStarted ? false : scrollable}
      chatStarted={chatStarted}
    >
      {modals}
      <LayoutWrapper chatStarted={chatStarted}>
        <ContentWrapper chatStarted={chatStarted}>
          <ChatScrollArea chatStarted={chatStarted}>
            <ChatHeading chatStarted={chatStarted}>
              {chatHeading && chatHeading}
            </ChatHeading>
            {/* Input box for initial state */}
            {!chatStarted && <div style={{ 
              display: "flex", 
              justifyContent: "center", 
              padding: "20px",
              paddingTop:"0px",
              width: "100%"
            }}>
              <div style={{ 
                width: "min(800px, 90%)",
                maxWidth: "800px"
              }}>
                {inputBox && inputBox}
              </div>
            </div>}
            <ChatContent chatStarted={chatStarted}>
              {chatContent && chatContent}
            </ChatContent>
            {/* Chat messages */}
            <ChatBox chatStarted={chatStarted}>
              {chatComponent && chatComponent}
            </ChatBox>
          </ChatScrollArea>
        </ContentWrapper>
      </LayoutWrapper>
      
      {/* Fixed input box for active chat */}
      {chatStarted && inputBox && (
        <FixedInputBox modalOpen={modalOpen}>
          <div>
            {inputBox}
          </div>
        </FixedInputBox>
      )}
      
      {false && <OverlayHide />}

      <Popup
        content="Scroll to bottom"
        inverted
        position="top center"
        trigger={
          <ScrollToBottomButton
            show={showScrollToBottom}
            chatStarted={chatStarted}
            modalOpen={modalOpen}
            onClick={scrollToBottom}
          >
            <Icon
              name={ICON_SET.dropdown}
              size={20}
              style={{ cursor: "pointer", backgroundColor: "white", opacity: 1, zIndex: 1000 }}
            />
          </ScrollToBottomButton>
        }
      />
    </BaseTemplate>
  );
};

ChatTemplate.propTypes = {
  heading: PropTypes.string,
  pageTitle: PropTypes.string,
  subtitle: PropTypes.string,
  onSend: PropTypes.func,
  scrollable: PropTypes.bool,
  chatHeading: PropTypes.node,
  chatContent: PropTypes.node,
  chatStarted: PropTypes.bool,
  chatComponent: PropTypes.node,
  fromPulseSurvey: PropTypes.bool,
};

ChatTemplate.defaultProps = {
  scrollable: false,
  fromPulseSurvey: false,
};

export default ChatTemplate;